// useSheetDrag — drag a phone bottom sheet down to close it, like iOS.
//
// Modal owns the sheet; this hook owns only the gesture. It never closes
// anything itself: on a dismissing release it calls `onDismiss(dy, backdropOpacity)`
// and Modal sends that through its single closer (attemptClose), so a dirty
// form still asks before it discards. Whatever Modal decides, it calls
// `springBack()` (blocked / refused) or lets its exit animation take over from
// the current offset (proceeding).
//
// Where a drag may start:
//   - the header/grabber, always (touch-action: none there);
//   - the scrolling body, only when body.scrollTop <= 0 AND the first real move
//     is downward. The body keeps native scrolling, so a non-cancelable pan
//     would end the gesture with pointercancel; once we have decided to drag we
//     therefore preventDefault the touchmove (non-passive listener below).
// Never on an input, button, select, textarea, link or [role=button].
//
// Not counted: a sideways or upward first move in the body (that is a scroll),
// a gesture that starts inside a scrolled inner scroller, a second finger.

import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react'
import { prefersReducedMotion } from '@/lib/motion'
import { classifyMove, isPullDownAtTop, rubberBand, shouldDismiss, trimMoves, velocity, type Move } from './sheetDragMath'

const SPRING_MS = 350
const SPRING = `transform ${SPRING_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`
const BACKDROP_DIM = 0.6

const IGNORE = 'input, button, select, textarea, a, [role="button"], [contenteditable="true"]'

/** Pointer capture keeps move/up events coming when the finger leaves the element. */
function capturePointer(e: ReactPointerEvent<HTMLElement>): void {
  try { (e.currentTarget as Element).setPointerCapture?.(e.pointerId) } catch { /* pointer already gone */ }
}

function releasePointer(e: ReactPointerEvent<HTMLElement>): void {
  try { (e.currentTarget as Element).releasePointerCapture?.(e.pointerId) } catch { /* already released */ }
}

function isSheet(): boolean {
  // Same test playExit uses: no matchMedia (jsdom) reads as a phone.
  return !(window.matchMedia?.('(min-width: 640px)').matches ?? false)
}

/** True when `el` or an ancestor below `stop` is a vertical scroller that has scrolled. */
function inScrolledScroller(el: Element | null, stop: Element | null): boolean {
  for (let n = el; n && n !== stop; n = n.parentElement) {
    if (n.scrollTop > 0) return true
  }
  return false
}

/** A drag may not start on a control, nor in a body that has already scrolled. */
function isIgnoredStart(from: 'header' | 'body', target: Element, body: Element | null): boolean {
  if (target.closest?.(IGNORE)) return true
  return from === 'body' && ((body?.scrollTop ?? 0) > 0 || inScrolledScroller(target, body))
}

interface Gesture {
  id: number
  startY: number
  startX: number
  /** Where the pointer was when the drag was recognised (body drags start late). */
  originY: number
  from: 'header' | 'body'
  dragging: boolean
  /** Decided that this gesture is not ours (a scroll, an ignored target). */
  ignored: boolean
  moves: Move[]
  dy: number
  height: number
}

interface Options {
  cardRef: { current: HTMLElement | null }
  backdropRef: { current: HTMLElement | null }
  bodyRef: { current: HTMLElement | null }
  /** False while pending / submitting / exiting / undismissable. */
  enabled: boolean
  onDismiss: (dy: number, backdropOpacity: number) => void
}

export function useSheetDrag({ cardRef, backdropRef, bodyRef, enabled, onDismiss }: Options) {
  const g = useRef<Gesture | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled
  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss

  const clear = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null }
    const card = cardRef.current
    const back = backdropRef.current
    if (card) {
      card.style.transform = ''
      card.style.transition = ''
    }
    if (back) { back.style.opacity = ''; back.style.transition = '' }
  }, [cardRef, backdropRef])

  /** Animate back to rest, then leave no inline style behind. */
  const springBack = useCallback(() => {
    const card = cardRef.current
    const back = backdropRef.current
    if (!card) return
    if (prefersReducedMotion()) { clear(); return }
    card.style.transition = SPRING
    card.style.transform = 'translateY(0px)'
    if (back) { back.style.transition = `opacity ${SPRING_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`; back.style.opacity = '1' }
    if (timer.current) clearTimeout(timer.current)
    // A timer, not transitionend: that never fires if nothing moved (dy was 0).
    timer.current = setTimeout(clear, SPRING_MS + 40)
  }, [cardRef, backdropRef, clear])

  useEffect(() => clear, [clear])

  // The body's native pan would cancel the pointer stream. Refuse it once we
  // are dragging, AND on any downward move of a body gesture at the top: a
  // browser commits to a pan on its first uncancelled touchmove, which on a
  // real phone comes before our SLOP is crossed, so waiting for `dragging`
  // would lose every body drag to pointercancel. Non-passive on purpose.
  useEffect(() => {
    const card = cardRef.current
    if (!card) return
    const stop = (e: TouchEvent) => {
      const s = g.current
      if (!s || s.ignored || !e.cancelable) return
      const t = e.touches[0]
      const dy = t ? t.clientY - s.startY : 0
      const dx = t ? t.clientX - s.startX : 0
      // Mostly vertical only, so a sideways swipe on an inner strip still scrolls.
      if (s.dragging || isPullDownAtTop(s.from, bodyTop(), dx, dy)) e.preventDefault()
    }
    card.addEventListener('touchmove', stop, { passive: false })
    return () => card.removeEventListener('touchmove', stop)
  }, [cardRef, bodyRef])

  const bodyTop = () => bodyRef.current?.scrollTop ?? 0

  const apply = (s: Gesture, rawDy: number) => {
    const card = cardRef.current
    const back = backdropRef.current
    if (!card) return
    const dy = rubberBand(rawDy)
    s.dy = dy
    card.style.transition = 'none'
    card.style.transform = `translateY(${dy}px)`
    if (back) {
      const progress = Math.min(1, Math.max(0, dy / (s.height || 1)))
      back.style.transition = 'none'
      back.style.opacity = String(1 - progress * BACKDROP_DIM)
    }
  }

  const begin = (from: 'header' | 'body') => (e: ReactPointerEvent<HTMLElement>) => {
    if (!enabledRef.current || !e.isPrimary || g.current || e.button > 0) return
    if (!isSheet()) return
    const card = cardRef.current
    if (!card) return
    const target = e.target as Element
    const ignored = isIgnoredStart(from, target, bodyRef.current)
    if (timer.current) { clearTimeout(timer.current); timer.current = null }
    g.current = {
      id: e.pointerId, startY: e.clientY, startX: e.clientX, originY: e.clientY, from,
      dragging: false, ignored, moves: [{ t: e.timeStamp, y: e.clientY }], dy: 0,
      height: card.getBoundingClientRect().height,
    }
  }

  /** Decide whether a not-yet-dragging gesture starts dragging now. False = keep waiting or give up. */
  const recognise = (s: Gesture, e: ReactPointerEvent<HTMLElement>): boolean => {
    // Body: the first real move must be downward and mostly vertical.
    const kind = classifyMove(s.from, e.clientX - s.startX, e.clientY - s.startY, bodyTop())
    if (kind === 'ignore') s.ignored = true
    if (kind !== 'drag') return false
    s.dragging = true
    if (s.from === 'body') s.originY = e.clientY
    capturePointer(e)
    return true
  }

  const move = (e: ReactPointerEvent<HTMLElement>) => {
    const s = g.current
    if (!s || s.ignored || e.pointerId !== s.id) return
    if (!enabledRef.current) { g.current = null; clear(); return }
    s.moves.push({ t: e.timeStamp, y: e.clientY })
    trimMoves(s.moves, e.timeStamp)
    if (!s.dragging && !recognise(s, e)) return
    apply(s, e.clientY - s.originY)
  }

  const end = (cancelled: boolean) => (e: ReactPointerEvent<HTMLElement>) => {
    const s = g.current
    if (!s || e.pointerId !== s.id) return
    g.current = null
    releasePointer(e)
    if (!s.dragging) return
    if (cancelled) { springBack(); return }
    if (shouldDismiss(s.dy, s.height, velocity(s.moves))) {
      const back = backdropRef.current
      const op = back ? Number(back.style.opacity || 1) : 1
      onDismissRef.current(s.dy, op)
    } else {
      springBack()
    }
  }

  const common = { onPointerMove: move, onPointerUp: end(false), onPointerCancel: end(true) }
  return {
    springBack,
    clear,
    headerProps: { onPointerDown: begin('header'), ...common, style: { touchAction: 'none' as const } },
    bodyProps: { onPointerDown: begin('body'), ...common },
  }
}
